import type { FC } from 'react';
import type { NodeProgressStatus, NodeTimeStatus } from '../../utils/nodeStatus';
import { getNodeProgressLabel, nodeStatusTitle } from '../../utils/nodeStatus';
import { milestoneStatusLabel } from '../../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../../utils/useIpcStatusEnums';

/** 展示用进度：编辑器三态 + 只读「已终止」 */
export type IpcNodeStatusProgress = NodeProgressStatus | 'terminated';

export type IpcNodeStatusProps = {
  progress: IpcNodeStatusProgress;
  time?: NodeTimeStatus;
  /** 默认 true；极窄列可 false 仅显示圆点 */
  showLabel?: boolean;
  compact?: boolean;
  className?: string;
  title?: string;
};

const DOT_CLASS: Record<IpcNodeStatusProgress, string> = {
  pending: 'ipc-node-dot--pending',
  in_progress: 'ipc-node-dot--active',
  completed: 'ipc-node-dot--completed',
  terminated: 'ipc-node-dot--terminated',
};

const TAG_CLASS: Record<IpcNodeStatusProgress, string> = {
  pending: 'ipc-node-tag--pending',
  in_progress: 'ipc-node-tag--active',
  completed: 'ipc-node-tag--completed',
  terminated: 'ipc-node-tag--terminated',
};

/**
 * 节点状态（原型 component-showcase · 节点状态）
 * 进度三态（圆点 + Tag）+ 时间维度（延期红环）；已终止为红色只读展示
 */
export const IpcNodeStatus: FC<IpcNodeStatusProps> = ({
  progress,
  time = 'normal',
  showLabel = true,
  compact = false,
  className,
  title,
}) => {
  useIpcStatusEnums();
  const delayed = time === 'delayed' && progress !== 'terminated';
  const label =
    progress === 'terminated'
      ? milestoneStatusLabel('terminated')
      : getNodeProgressLabel(progress);
  const tooltip =
    title
    ?? (progress === 'terminated'
      ? label
      : nodeStatusTitle(progress, time));

  return (
    <span
      className={[
        'ipc-node-status',
        compact ? 'ipc-node-status--compact' : '',
        delayed ? 'ipc-node-status--delayed' : '',
        className,
      ].filter(Boolean).join(' ')}
      title={tooltip}
    >
      <span className="ipc-node-dot-shell" aria-hidden>
        <span className={['ipc-node-dot', DOT_CLASS[progress]].join(' ')} />
      </span>
      {showLabel ? (
        <span className={['ipc-node-tag', TAG_CLASS[progress]].join(' ')}>
          {label}
        </span>
      ) : null}
    </span>
  );
};
